import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { mapConstructionNumbers, mapHousingTypes } from '../lib/mapping';
import { useModulesQuery } from '../../modules/services/apollo/apollo';
import { GET_SUB_PROJECT } from '../../modules/services/apollo/queries/getSubProject';
import { rootHousingTypeId, rootSubProjectId } from '../lib/root';
import { getPrices } from '../lib/helpers';

export const DataContext = createContext();

export const DataContextProvider = ({ children }) => {
  const [isRental, setIsRental] = useState(false);
  const [subProject, setSubProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [subProjectStatus, setSubProjectStatus] = useState();
  const [hidePrices, setHidePrices] = useState(true);
  const [constructionNumbers, setConstructionNumbers] = useState([]);
  const [filteredConstructionNumbers, setFilteredConstructionNumbers] = useState([]);
  const [housingTypes, setHousingTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [availableFilters, setAvailableFilters] = useState({
    priceMin: 150000, /** todo: replace with '750' when switching to borgdonck data */
    priceMax: 1500000, /** todo: replace with '1500' when switching to borgdonck data */
    metricsLiving: [75, 100, 150, 175, 200],
    metricsLot: [75, 150, 200, 250, 300],
    housingKinds: [],
    state: ['Verwacht', 'Te huur', 'Verhuurd']
  });
  const initialActiveFilters = {
    priceMin: 150000,
    priceMax: 1500000,
    metricsLiving: null,
    metricsLot: null,
    housingKinds: [],
    housingTypes: [],
    state: []
  };
  const [activeFilters, setActiveFilters] = useState(initialActiveFilters);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [listItemsCount, setListItemsCount] = useState(10);
  const [sortOptions] = useState({
    typeAsc: 'Type oplopend',
    typeDesc: 'type aflopend',
    addressAsc: 'adres oplopend',
    addressDesc: 'adres aflopend',
    housingKindAsc: 'Soort van A naar Z',
    housingKindDesc: 'Soort van Z naar A',
    priceAsc: 'Prijs oplopend',
    priceDesc: 'Prijs aflopend',
    metricsLiningAsc: 'Gebruiksopp. oplopend',
    metricsLivingDesc: 'Gebruiksopp. aflopend',
    metricsLotAsc: 'Perceelopp. oplopend',
    metricsLotDesc: 'Perceelopp. aflopend'
  });
  const [sortBy, setSortBy] = useState();

  // GET DATA
  useModulesQuery(GET_SUB_PROJECT, {
    variables: {
      id: parseInt(rootSubProjectId, 10)
    },
    onError: (error) => {
      throw new Error(error);
    },
    onCompleted: (res) => {
      // Handle data
      const { getSubProject } = res;
      setSubProjectStatus(getSubProject.status);
      setHidePrices(getSubProject.hidePrices);

      const { children: subProjectChildren } = getSubProject;
      const [constructionNumbersFolder, housingTypesFolder] = subProjectChildren;

      setIsRental(getSubProject.isRental || false);
      setSubProject(getSubProject);

      if (getSubProject.isRental) {
        if (getSubProject.status === 'Verhuurd') {
          setIsDisabled(true);
        }
        setAvailableFilters({
          priceMin: 500,
          priceMax: 5000,
          metricsLiving: [75, 100, 150, 175, 200],
          metricsLot: [75, 150, 200, 250, 300],
          housingKinds: [],
          state: ['Verwacht', 'Te huur', 'Verhuurd']
        });
      } else {
        setAvailableFilters({
          priceMin: 150000,
          priceMax: 1500000,
          metricsLiving: [75, 100, 150, 175, 200],
          metricsLot: [75, 150, 200, 250, 300],
          housingKinds: [],
          state: ['Beschikbaar', 'In optie', 'Verkocht']
        });
      }

      const mappedConstructionNumbers = mapConstructionNumbers(constructionNumbersFolder.children);
      const mappedHousingTypes = mapHousingTypes(
          housingTypesFolder.children,
          mappedConstructionNumbers,
          getSubProject.isRental /** not sure why I cannot use isRental */
      );

      setConstructionNumbers([...mappedConstructionNumbers]);
      setHousingTypes([...mappedHousingTypes]);

      setAvailableFilters((prevState) => ({
        ...prevState,
        housingKinds: [...new Set(mappedConstructionNumbers.map((item) => item.kind))]
      }));
      if (rootHousingTypeId && !activeFilters.housingTypes.includes(rootHousingTypeId)) {
        setActiveFilters((prevState) => ({
          ...prevState,
          housingTypes: [...prevState.housingTypes, rootHousingTypeId]
        }));
      }
      setIsLoading(false);
    }
  });

  useEffect(() => {
    function sortConstructionNumbers(input) {
      return input
      .sort((a, b) => availableFilters.state.indexOf(a.state) - availableFilters.state.indexOf(b.state))
      .sort((a, b) => {
        if ((sortBy === 'typeAsc' && a.housingVariant < b.housingVariant)
            || (sortBy === 'typeDesc' && a.housingVariant > b.housingVariant)
            || (sortBy === 'addressAsc' && a.address < b.address)
            || (sortBy === 'addressDesc' && a.address > b.address)
            || (sortBy === 'housingKindAsc' && a.kind < b.kind)
            || (sortBy === 'housingKindDesc' && a.kind > b.kind)
            || (sortBy === 'metricsLiningAsc' && a.metrics.living < b.metrics.living)
            || (sortBy === 'metricsLiningDesc' && a.metrics.living > b.metrics.living)
            || (sortBy === 'metricsLotAsc' && a.metrics.parcel < b.metrics.parcel)
            || (sortBy === 'metricsLotDesc' && a.metrics.parcel > b.metrics.parcel)
            || (sortBy === 'priceAsc' && getPrices(a) < getPrices(b))
            || (sortBy === 'priceDesc' && getPrices(a) > getPrices(b))
        ) {
          return -1;
        }

        return 0;
      });
    }

    if (!constructionNumbers.length) return;

    /** in rental projects there are no filters available */
    if (isRental) {
      setFilteredConstructionNumbers(sortConstructionNumbers(constructionNumbers));
      return;
    }

    /** filters applied for non-rental tables */
    const filtered = constructionNumbers.filter((item) => (activeFilters.housingTypes.length ? activeFilters.housingTypes.includes(item.typeId) : true))
      .filter((item) => (sortBy === 'priceAsc' ? item.state !== 'Verkocht' : true))
      .filter((item) => (sortBy === 'priceDesc' ? item.state !== 'Verkocht' : true))
      .filter((item) => (activeFilters.housingKinds.length ? activeFilters.housingKinds.includes(item.kind) : true))
      .filter((item) => (activeFilters.state.length ? activeFilters.state.includes(item.state) : true))
      .filter((item) => (activeFilters.metricsLiving ? item.metrics.living >= activeFilters.metricsLiving : true))
      .filter((item) => (activeFilters.metricsLot ? item.metrics.parcel >= activeFilters.metricsLot : true))
      .filter((item) => {
        if (item.prices.definitive) {
          return item.prices.definitive >= +activeFilters.priceMin && item.prices.definitive <= +activeFilters.priceMax;
        }
        if (item.prices.indicative) {
          return item.prices.indicative >= +activeFilters.priceMin && item.prices.indicative <= +activeFilters.priceMax;
        }
        return item;
      });

    setFilteredConstructionNumbers(sortConstructionNumbers(filtered));
  }, [constructionNumbers, activeFilters, availableFilters.state, sortBy, isRental, availableFilters]);

  useEffect(() => {
    let count = 0;
    Object.entries(activeFilters).map((item) => {
      if (Array.isArray(item[1])) {
        count += item[1].length;
      } else if (initialActiveFilters[item[0]] !== null && +item[1] !== +initialActiveFilters[item[0]]) {
          count += 1;
      }
      setActiveFiltersCount(count);

       return null;
    });
  }, [activeFilters, initialActiveFilters]);

  return (
    <DataContext.Provider
      value={{
        isLoading,
        constructionNumbers,
        filteredConstructionNumbers,
        housingTypes,
        showFilters,
        setShowFilters,
        availableFilters,
        initialActiveFilters,
        activeFilters,
        setActiveFilters,
        activeFiltersCount,
        listItemsCount,
        setListItemsCount,
        sortOptions,
        sortBy,
        setSortBy,
        subProjectStatus,
        hidePrices,
        isRental,
        subProject,
        isDisabled
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

DataContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useDataContext = () => useContext(DataContext);
